/** @module store/ems_meta_infos */

import * as apiEmsMetaInfos from '@/api/ems_meta_infos'

export const VALID_EMS_META_INFO_TAGS = ['GRID_PRICES']

const state = () => ({
  emsToEmsMetaInfo: {}
})

const mutations = {
  DELETE_EMS_META_INFO: (state, { emsId, tag }) => {
    delete state.emsToEmsMetaInfo[emsId]?.[tag]

    if (state.emsToEmsMetaInfo[emsId] && !Object.entries(state.emsToEmsMetaInfo[emsId]).length)
      delete state.emsToEmsMetaInfo[emsId]
  },
  SET_EMS_META_INFO: (state, { emsMetaInfo }) => {
    if (!(emsMetaInfo instanceof EmsMetaInfo)) return

    if (!state.emsToEmsMetaInfo[emsMetaInfo.energyManagementSystemId])
      state.emsToEmsMetaInfo[emsMetaInfo.energyManagementSystemId] = {}

    state.emsToEmsMetaInfo[emsMetaInfo.energyManagementSystemId][emsMetaInfo.tag] = emsMetaInfo
  },
  SET_EMS_TO_EMS_META_INFOS: (state, { emsMetaInfos }) => {
    state.emsToEmsMetaInfo = emsMetaInfos
      .filter((info) => info instanceof EmsMetaInfo)
      .reduce((prev, info) => {
        if (!prev[info.energyManagementSystemId]) prev[info.energyManagementSystemId] = {}

        prev[info.energyManagementSystemId][info.tag] = info

        return prev
      }, {})
  }
}

const getters = {
  getEmsMetaInfo: (state) => (emsId, tag) => state.emsToEmsMetaInfo[emsId]?.[tag]
}

const actions = {
  createEmsMetaInfo: ({ commit }, { ctxt, emsId, tag, data }) =>
    apiEmsMetaInfos
      .createEmsMetaInfo({ ctxt, emsId, tag, data })
      .then((msg) => commit('SET_EMS_META_INFO', { emsMetaInfo: resourceToEmsMetaInfo(msg?.payload?.data) })),
  deleteEmsMetaInfo: ({ commit, getters }, { ctxt, emsId, tag }) => {
    const id = getters.getEmsMetaInfo(emsId, tag)?.id

    if (!id) return

    return apiEmsMetaInfos
      .deleteEmsMetaInfo({ ctxt, emsId, id })
      .then(() => commit('DELETE_EMS_META_INFO', { emsId, tag }))
  },
  getEmsMetaInfos: ({ commit }, { ctxt, emsId, tags }) =>
    apiEmsMetaInfos.getEmsMetaInfos({ ctxt, emsId, tags }).then((msg) =>
      commit('SET_EMS_TO_EMS_META_INFOS', {
        emsMetaInfos: msg?.payload?.data.map((res) => resourceToEmsMetaInfo(res))
      })
    ),
  updateEmsMetaInfo: ({ commit, getters }, { ctxt, emsId, tag, data }) => {
    const id = getters.getEmsMetaInfo(emsId, tag)?.id

    if (!id) return

    return apiEmsMetaInfos
      .updateEmsMetaInfo({ ctxt, emsId, id, data })
      .then((msg) => commit('SET_EMS_META_INFO', { emsMetaInfo: resourceToEmsMetaInfo(msg?.payload?.data) }))
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}

/**
 * A EmsMetaInfo data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {string} params.id is the unique identifier of the EmsMetaInfo
 * @param {integer} params.energyManagementSystemId referenced EMS
 * @param {string} params.tag is the info tag. has to be on of VALID_EMS_META_INFO_TAGS
 * @param {string} params.data a standard object data blob
 *
 */
export class EmsMetaInfo {
  constructor({ id, energyManagementSystemId, tag, data }) {
    if (!id) throw new TypeError('An Id has to be present')
    if (!energyManagementSystemId) throw new TypeError('An EMS-ID has to be present')
    if (!VALID_EMS_META_INFO_TAGS.includes(tag)) throw new TypeError(`${tag} is not a valid EmsMetaInfo tag`)

    this.id = id
    this.energyManagementSystemId = energyManagementSystemId
    this.tag = tag
    this.data = data || {}
  }

  getData(key) {
    return this.data[key]
  }
}

const resourceToEmsMetaInfo = (res) => {
  return new EmsMetaInfo({
    id: res.id,
    energyManagementSystemId: res.relationships?.energyManagementSystem?.data?.id,
    tag: res.attributes?.tag,
    data: res.attributes?.data
  })
}
